import { Fragment } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Loading from '../loading';
import ErrorMessage from '../error-message';
import NotFound from '../not-found';
import InviteList from '../invite-list';
import {
  eventInvitesQuery,
  expireEventInviteMutation,
  extendEventInviteMutation,
  resendEventEmailInviteMutation
} from '../../apollo/events';
import NewLinkInvite from './new-link-invite';
import NewEmailInvites from './new-email-invites';
import CopyInput from '../copy-input';
import config from '../../config';
import { eventPath } from '../../utils/slugify';
import type { Event } from '../../types/Event';

type Props = {
  event?: Event;
};

const EventInvitesTab = (props: Props) => {
  const { event: eventProp } = props;

  const { data, loading, error } = useQuery(eventInvitesQuery, {
    variables: {
      id: eventProp.id
    }
  });
  const event = data?.event;

  const [expireEventInvite] = useMutation(expireEventInviteMutation);
  const [extendEventInvite] = useMutation(extendEventInviteMutation);
  const [resendEventEmailInvite] = useMutation(resendEventEmailInviteMutation);

  if (error) {
    return <ErrorMessage message='An error occurred while loading invites.' error={error} compact />;
  }
  if (loading) { return <Loading compact />; }
  if (!event || !event.permissions.editable) { return <NotFound compact />; }

  const inviteListProps = {
    basePath: eventPath(event.id, event.name),
    expireInvite: expireEventInvite,
    extendInvite: extendEventInvite,
    resendEmailInvite: resendEventEmailInvite
  };

  return (
    <Fragment>
      {event.public && (
        <Alert severity="info" icon={false} sx={{ my: 2 }}>
          <p style={{ marginTop: 0 }}>Special invites are not required for public events. Anyone with a link to the event can join.</p>
          <CopyInput text={`${config().universal.baseUri}${eventPath(event.id, event.name)}`} />
        </Alert>
      )}

      <Typography variant="h2">Link Invites</Typography>
      <InviteList invites={event.linkInvites} {...inviteListProps} />
      <NewLinkInvite event={event} />

      <Typography variant="h2">Email Invites</Typography>
      <InviteList invites={event.emailInvites} {...inviteListProps} />
      <NewEmailInvites event={event} />
    </Fragment>
  );
};

export default EventInvitesTab;
